import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Smartphone, Monitor } from "lucide-react";

interface NewsletterPreviewProps {
  content: string;
  isLoading?: boolean;
}

export const NewsletterPreview = ({ content, isLoading }: NewsletterPreviewProps) => {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const processContent = (rawContent: string): string => {
    try {
      if (!rawContent) return '';
      
      // If it's already HTML, return as is
      if (rawContent.trim().startsWith('<')) {
        return rawContent;
      }

      // Try to parse as JSON if it's a JSON string
      try {
        const parsed = JSON.parse(rawContent);
        if (typeof parsed === 'object') {
          // Check if it has a template property
          if ('template' in parsed) {
            const templateContent = parsed.template;
            // Replace variables with sample data
            let processedContent = typeof templateContent === 'string' ? templateContent : JSON.stringify(templateContent, null, 2);
            
            // Replace common variables with sample data
            processedContent = processedContent
              .replace(/\{DYNAMIC_TOPIC\}/g, 'Latest EV Market Updates')
              .replace(/\{USER_NAME\}/g, 'John')
              .replace(/\{COMPANY_NAME\}/g, 'EVscoop')
              .replace(/\{DATE\}/g, new Date().toLocaleDateString());
              
            return processedContent;
          }
          // If no template property, stringify the entire object
          return JSON.stringify(parsed, null, 2);
        }
      } catch {
        // Not JSON, continue with markdown processing
      }

      // Convert markdown-style content to basic HTML
      return rawContent
        .split('\n')
        .map(line => {
          if (line.startsWith('# ')) {
            return `<h1 class="text-2xl font-bold mb-4">${line.substring(2)}</h1>`;
          }
          if (line.startsWith('## ')) {
            return `<h2 class="text-xl font-semibold mb-3">${line.substring(3)}</h2>`;
          }
          if (line.startsWith('- ')) {
            return `<li class="ml-4">${line.substring(2)}</li>`;
          }
          if (line.trim() === '') {
            return '<br/>';
          }
          return `<p class="mb-2">${line}</p>`;
        })
        .join('\n');
    } catch (error) {
      console.error('Error processing content:', error);
      return rawContent || '';
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Newsletter Preview</CardTitle>
          <div className="flex space-x-2">
            <Button
              variant={viewMode === 'desktop' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('desktop')}
            >
              <Monitor className="h-4 w-4 mr-2" />
              Desktop
            </Button>
            <Button
              variant={viewMode === 'mobile' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('mobile')}
            >
              <Smartphone className="h-4 w-4 mr-2" />
              Mobile
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div 
          className={`preview-container ${
            viewMode === 'mobile' ? 'max-w-[375px] mx-auto' : 'w-full'
          }`}
        >
          <div 
            className="preview-content prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: processContent(content) }}
          />
        </div>
      </CardContent>
    </Card>
  );
};